import { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import moment from "moment";
import { QICustomSelect, QICustomDateRangePicker, QIButton, QIInput, QITimePicker, QIMultiSelectDropDown, QISwitch } from "../../../../components";
import {
  useGetScheduledEmailConfigurationsQuery,
  useCreateScheduledEmailConfigurationMutation,
  useUpdateScheduledEmailConfigurationMutation,
  useGetUserGroupsQuery,
  useGetVehicleGroupsQuery,
  useGetDeviceGroupsQuery,
  useGetVehiclesQuery,
  useGetDevicesQuery,
  useGetEventTypesQuery,
  useGetReportConfigurationsQuery,
} from "../../services";
import { SvgIcon } from "../../../Shared";
import { getLocalizedString } from "../../../../shared/translation";
import { CurrentUser } from "../../../../shared/currentUser";
import { FMDPStorage, parseServerError } from "../../../../shared/helper";
import { useDropDownSearch } from "../../../../hooks";
import { reportTypes } from "../../../../shared/reportTypes";
import { selectionType, selectionTypeNoGroup, assetTypes, assetTypesNoDevice, fileFormats, subTypes, subTypesNoSummary, summaryIntervals } from "../../../Tracking/Reports/static";
import { hasAdminRole, getUserIdForFiltering } from "../../../Tracking/utils";
import { buildScheduledEmailPayload, getScheduledReportValues, validateScheduledReportValues } from "./scheduledEmail";

const DAYS_OF_WEEK = [
  { label: "S", value: "sunday" },
  { label: "M", value: "monday" },
  { label: "T", value: "tuesday" },
  { label: "W", value: "wednesday" },
  { label: "T", value: "thursday" },
  { label: "F", value: "friday" },
  { label: "S", value: "saturday" },
];

const FREQUENCY_OPTIONS = [
  { label: "Never", value: "never" },
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];

const WEEK_OPTIONS = [
  { label: "First", value: "first" },
  { label: "Second", value: "second" },
  { label: "Third", value: "third" },
  { label: "Fourth", value: "fourth" },
  { label: "Last", value: "last" },
];

const WEEK_OPTIONS_DATA = [
  { id: "first", name: "First Week" },
  { id: "second", name: "Second Week" },
  { id: "third", name: "Third Week" },
  { id: "fourth", name: "Fourth Week" },
  { id: "last", name: "Last Week" },
];

const DATE_RANGE_OPTIONS = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Custom", value: "custom" },
];

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
const FieldError = ({ error }) => error ? <p className="text-danger small mt-1 mb-3" role="alert">{error}</p> : null;

/**
 * Format selected days as ranges when consecutive.
 * e.g. ["monday","tuesday","wednesday"] → "Monday–Wednesday"
 *      ["monday","wednesday","friday"]   → "Monday, Wednesday, Friday"
 *      ["monday","tuesday","thursday","friday"] → "Monday–Tuesday, Thursday–Friday"
 */
const formatDayRanges = (selectedDays) => {
  if (!selectedDays || selectedDays.length === 0) return "";
  const dayOrder = DAYS_OF_WEEK.map((d) => d.value);
  const indices = selectedDays
    .map((d) => dayOrder.indexOf(d))
    .filter((i) => i !== -1)
    .sort((a, b) => a - b);
  if (indices.length === 0) return "";

  const ranges = [];
  let start = indices[0];
  let end = indices[0];

  for (let i = 1; i < indices.length; i++) {
    if (indices[i] === end + 1) {
      end = indices[i];
    } else {
      ranges.push([start, end]);
      start = indices[i];
      end = indices[i];
    }
  }
  ranges.push([start, end]);

  return ranges
    .map(([s, e]) =>
      s === e
        ? capitalize(dayOrder[s])
        : `${capitalize(dayOrder[s])}–${capitalize(dayOrder[e])}`
    )
    .join(", ");
};

export const ScheduledReportDelivery = () => {
  const dropdownParams = { per_page: 1000, order_by: "name" };
  const [editMode, setEditMode] = useState(false);

  const currentUser = new CurrentUser("Email Notification Configurations");
  const clientId = FMDPStorage.get("selected-client-id");
  const user = FMDPStorage.get("current-user");
  const appId = FMDPStorage.get("selected-app-id");
  const isAdmin = hasAdminRole(user, appId);

  const { data: configData } = useGetScheduledEmailConfigurationsQuery(
    { client_id: clientId }, { skip: !clientId }
  );
  const { data: userGroups, isSuccess: isUserGroupsSuccess } =
    useGetUserGroupsQuery(dropdownParams);

  const [createConfig] = useCreateScheduledEmailConfigurationMutation();
  const [updateConfig] = useUpdateScheduledEmailConfigurationMutation();

  const existingConfig = configData?.data?.[0] || null;

  const formik = useFormik({
    initialValues: getScheduledReportValues(null),
    enableReinitialize: true,
    onSubmit: (values) => handleSubmit(values),
  });

  const { data: eventTypes } = useGetEventTypesQuery(
    { per_page: 100 }, { skip: formik.values.report_type !== "event" }
  );
  const { data: reportConfigurations } = useGetReportConfigurationsQuery(
    { per_page: 1000, q: JSON.stringify({ report_type_eq: reportTypes.findIndex((type) => type.id === "trackpoint") }) },
    { skip: formik.values.report_type !== "trackpoint" || formik.values.report_sub_type !== "detailed" }
  );

  const assetIds = formik.values.metadata.asset_ids;
  const criteria = formik.values.metadata.selection_criteria;
  const assetType = formik.values.metadata.asset_type;
  const isGroup = criteria === "Group";
  const isDevice = assetType === "Device";
  const { data: vehicleGroups, setSearchKey: searchVehicleGroups } = useDropDownSearch({
    useData: useGetVehicleGroupsQuery, simpleSearchKey: "name_cont", selectedIds: assetIds,
    additionalParams: { per_page: 600 },
    additionalParamsToHook: { skip: !isGroup || assetType !== "Vehicle" },
  });
  const { data: deviceGroups, setSearchKey: searchDeviceGroups } = useDropDownSearch({
    useData: useGetDeviceGroupsQuery, simpleSearchKey: "name_cont", selectedIds: assetIds,
    additionalParams: { per_page: 600 },
    additionalParamsToHook: { skip: !isGroup || !isDevice },
  });
  const { data: vehicles, setSearchKey: searchVehicles } = useDropDownSearch({
    useData: useGetVehiclesQuery, simpleSearchKey: "name_or_plate_number_cont", selectedIds: assetIds,
    additionalParams: { per_page: 600, filter_user_id: getUserIdForFiltering(user, appId) },
    additionalParamsToHook: { skip: criteria !== "Asset" || assetType !== "Vehicle" },
  });
  const { data: devices, setSearchKey: searchDevices } = useDropDownSearch({
    useData: useGetDevicesQuery, simpleSearchKey: "device_id_cont", additionalSearchKey: ["name_cont"],
    selectedIds: assetIds, additionalParams: { per_page: 600 },
    additionalParamsToHook: { skip: criteria !== "Asset" || !isDevice },
  });

  const assetOptions = isGroup
    ? isDevice ? deviceGroups?.data : vehicleGroups?.data
    : isDevice
      ? devices?.data?.map((item) => ({ ...item, name: item.device_id }))
      : vehicles?.data?.map((item) => ({ ...item, name: item.plate_number }));
  const formatOptions = formik.values.file_format.includes("csv_zip")
    ? [...fileFormats, { id: "csv_zip", name: "CSV ZIP" }]
    : fileFormats;
  const searchAssets = isGroup
    ? isDevice ? searchDeviceGroups : searchVehicleGroups
    : isDevice ? searchDevices : searchVehicles;

  const handleSubmit = async (formData) => {
    if (!clientId) {
      formik.setErrors({ serverError: "Select a client first." });
      return;
    }
    if (!existingConfig && formData.frequency === "never") {
      setEditMode(false);
      return;
    }
    const errors = validateScheduledReportValues(formData);
    if (Object.keys(errors).length) {
      formik.setErrors(errors);
      return;
    }

    try {
      const formDataPayload = buildScheduledEmailPayload(
        formData,
        existingConfig?.report_configuration_id
      );
      if (existingConfig?.id) {
        await updateConfig({ id: existingConfig.id, client_id: clientId, formData: formDataPayload }).unwrap();
      } else {
        await createConfig({ client_id: clientId, formData: formDataPayload }).unwrap();
      }
      setEditMode(false);
    } catch (error) {
      const serverErrors = parseServerError(error);
      formik.setErrors({
        user_group_ids: serverErrors.user_group_id,
        report_configuration_id: serverErrors.report_configuration_id,
        serverError: Object.entries(serverErrors)
          .filter(([field]) => field !== "user_group_id" && field !== "report_configuration_id")
          .map(([, message]) => message).join(" ") ||
          (serverErrors.report_configuration_id ? undefined :
            getLocalizedString("scheduled_report_config_error", "Something went wrong. Please try again later.")),
      });
    }
  };

  useEffect(() => {
    formik.setValues(getScheduledReportValues(existingConfig));
  }, [existingConfig]);

  const toggleEditMode = () => {
    if (editMode) {
      formik.setValues(getScheduledReportValues(existingConfig));
      setEditMode(false);
    } else {
      setEditMode(true);
    }
  };

  const toggleDay = useCallback((dayValue) => {
    if (!editMode) return;
    const current = formik.values.selected_days || [];
    formik.setFieldValue("selected_days",
      current.includes(dayValue) ? current.filter((d) => d !== dayValue) : [...current, dayValue]
    );
    formik.setFieldError("selected_days", undefined);
  }, [editMode, formik.values.selected_days]);

  const toggleWeek = useCallback((weekValue) => {
    if (!editMode) return;
    const current = formik.values.send_every_weeks || [];
    formik.setFieldValue("send_every_weeks",
      current.includes(weekValue) ? current.filter((w) => w !== weekValue) : [...current, weekValue]
    );
  }, [editMode, formik.values.send_every_weeks]);

  const showDaySelector = formik.values.frequency === "weekly" || formik.values.frequency === "monthly";
  const fieldsReadOnly = !editMode || formik.values.frequency === "never";

  return (
    <div className="customization_block scheduled-report-delivery">
      <header className="customization_block_header">
        <strong>{getLocalizedString("scheduled_report_email_delivery", "Scheduled Report Email Delivery")}</strong>
        {currentUser.canUpdate && (
          <span className="edit">
            <SvgIcon name="edit" wrapperClass="mx-2" onClick={toggleEditMode} />
          </span>
        )}
      </header>

      <div className="scheduled-report-delivery_content">
        {/* Banner */}
        <div className="scheduled-report-delivery_banner">
          <div className="scheduled-report-delivery_banner_info">
            <SvgIcon name="info" wrapperClass="info-icon" />
            <span>{getLocalizedString("set_schedule_recurring", "Set a schedule to make this report recurring")}</span>
          </div>
        </div>

        {/* Frequency - label inside via QICustomSelect label prop */}
        <QICustomSelect
          label={getLocalizedString("frequency", "Frequency")}
          value={formik.values.frequency}
          onChange={(value) => {
            formik.setFieldValue("frequency", value);
            formik.setFieldError("frequency", undefined);
          }}
          disabled={!editMode}
          readOnly={!editMode}
          error={formik.errors.frequency}
        >
          {FREQUENCY_OPTIONS.map((opt) => (
            <li key={opt.value} value={opt.value}>{opt.label}</li>
          ))}
        </QICustomSelect>
        <FieldError error={formik.errors.frequency} />

        {/* Day selector */}
        {showDaySelector && (
          <div className={`qi-input qi-form-element srd-day-selector-row ${fieldsReadOnly ? "disabled" : ""} ${formik.errors.selected_days ? "error" : ""}`} aria-invalid={!!formik.errors.selected_days}>
            <label className="qi-input_label">Day(s)</label>
            <div className="qi-input_wrapper">
              <div className="srd-day-selector">
                {DAYS_OF_WEEK.map((day, index) => (
                  <button
                    key={`${day.value}-${index}`}
                    type="button"
                    className={`srd-day-selector_btn ${formik.values.selected_days?.includes(day.value) ? "active" : ""}`}
                    title={capitalize(day.value)}
                    onClick={() => toggleDay(day.value)}
                    disabled={fieldsReadOnly}
                  >{day.label}</button>
                ))}
              </div>
              <FieldError error={formik.errors.selected_days} />
            </div>
          </div>
        )}

        {/* Send every - using QIMultiSelectDropDown */}
        {formik.values.frequency === "monthly" && (
          <div className="srd-send-every-row">
            <QIMultiSelectDropDown
              label={getLocalizedString("send_every", "Send every")}
              labelKey="name"
              data={WEEK_OPTIONS_DATA}
              allData={WEEK_OPTIONS_DATA}
              selected={formik.values.send_every_weeks}
              onChange={(ids) => {
                formik.setFieldValue("send_every_weeks", ids);
                formik.setFieldError("send_every_weeks", undefined);
              }}
              readOnly={fieldsReadOnly}
              error={formik.errors.send_every_weeks}
            />
            {formik.values.selected_days?.length > 0 && (
              <span className="srd-day-range-label">{formatDayRanges(formik.values.selected_days)}</span>
            )}
          </div>
        )}
        {formik.values.frequency === "monthly" && <FieldError error={formik.errors.send_every_weeks} />}

        <QITimePicker
          label={getLocalizedString("send_time", "Send time")}
          value={formik.values.send_time}
          onChange={(value) => {
            formik.setFieldValue("send_time", value);
            formik.setFieldError("send_time", undefined);
          }}
          readOnly={fieldsReadOnly}
          disabled={fieldsReadOnly}
          error={formik.errors.send_time}
        />
        <FieldError error={formik.errors.send_time} />

        <QICustomSelect
          label={getLocalizedString("date_range", "Date range")}
          value={formik.values.date_range}
          onChange={(value) => {
            if (value === "custom" && (!formik.values.start_time || !formik.values.end_time)) {
              const end = moment().startOf("minute");
              formik.setFieldValue("start_time", end.clone().subtract(1, "day").unix());
              formik.setFieldValue("end_time", end.unix());
            }
            formik.setFieldValue("date_range", value);
            formik.setFieldError("date_range", undefined);
          }}
          disabled={fieldsReadOnly}
          readOnly={fieldsReadOnly}
          error={formik.errors.date_range}
        >
          {DATE_RANGE_OPTIONS.map((opt) => (
            <li key={opt.value} value={opt.value}>{opt.label}</li>
          ))}
        </QICustomSelect>
        <FieldError error={formik.errors.date_range} />

        {formik.values.date_range === "custom" && (!fieldsReadOnly ? (
          <QICustomDateRangePicker
            label={getLocalizedString("custom_date_range", "Custom Date Range")}
            selectedRange={{
              startDate: moment.unix(formik.values.start_time),
              endDate: moment.unix(formik.values.end_time),
            }}
            setSelectedRangeLocal={({ startDate, endDate }) => {
              formik.setFieldValue("start_time", moment(startDate).unix());
              formik.setFieldValue("end_time", moment(endDate).unix());
            }}
            localReport
            className="srd-custom-date-range"
            showDefaultRanges={false}
            showReset={false}
            preventAutoUpdate
          />
        ) : (
          <QIInput
            label={getLocalizedString("custom_date_range", "Custom Date Range")}
            value={`${moment.unix(formik.values.start_time).format("DD/MM/YYYY HH:mm")} - ${moment.unix(formik.values.end_time).format("DD/MM/YYYY HH:mm")}`}
            readOnly
            disabled
          />
        ))}

        <QICustomSelect
          label={getLocalizedString("type", "Type")}
          value={formik.values.report_type}
          onChange={(value) => {
            formik.setFieldValue("report_type", value);
            formik.setFieldError("report_type", undefined);
            formik.setFieldValue("report_sub_type", "");
            formik.setFieldValue("event_type_id", "");
            formik.setFieldValue("event_type", "");
            formik.setFieldValue("report_configuration_id", "");
            formik.setFieldError("report_configuration_id", undefined);
            formik.setFieldValue("summary_interval", "");
            formik.setFieldValue("group_by_asset", false);
          }}
          readOnly={fieldsReadOnly}
          disabled={fieldsReadOnly}
          error={formik.errors.report_type}
        >
          {reportTypes.map((item) => (
            <li key={item.id} value={item.id}>{getLocalizedString(item.localization_key) || item.name}</li>
          ))}
        </QICustomSelect>
        <FieldError error={formik.errors.report_type} />

        <QICustomSelect
          label={getLocalizedString("subtype", "Sub-Type")}
          value={formik.values.report_sub_type}
          onChange={(value) => {
            formik.setFieldValue("report_sub_type", value);
            formik.setFieldError("report_sub_type", undefined);
            if (value !== "detailed") formik.setFieldValue("report_configuration_id", "");
            formik.setFieldError("report_configuration_id", undefined);
            formik.setFieldValue("summary_interval", "");
            formik.setFieldValue("group_by_asset", false);
          }}
          readOnly={fieldsReadOnly}
          disabled={fieldsReadOnly}
          error={formik.errors.report_sub_type}
        >
          {(formik.values.report_type === "raw" || formik.values.report_type === "offline_report" ? subTypesNoSummary : subTypes).map((item) => (
            <li key={item.id} value={item.id}>{getLocalizedString(item.localization_key) || item.name}</li>
          ))}
        </QICustomSelect>
        <FieldError error={formik.errors.report_sub_type} />

        {formik.values.report_type === "event" && (
          <>
            <QICustomSelect
              label={getLocalizedString("event_type", "Event Type")}
              value={formik.values.event_type_id}
              onChange={(value) => {
                const selectedType = eventTypes?.data?.find((type) => String(type.id) === String(value));
                formik.setFieldValue("event_type_id", value);
                formik.setFieldValue("event_type", selectedType?.name || "");
                formik.setFieldError("event_type_id", undefined);
              }}
              readOnly={fieldsReadOnly}
              disabled={fieldsReadOnly}
              error={formik.errors.event_type_id}
            >
              <li value="">{getLocalizedString("select", "Select")}</li>
              {eventTypes?.data?.map((type) => (
                <li key={type.id} value={type.id}>
                  {type.name.replace(/\b\w/g, (character) => character.toUpperCase())} ({type.classification})
                </li>
              ))}
            </QICustomSelect>
            <FieldError error={formik.errors.event_type_id} />
          </>
        )}

        {formik.values.report_type === "trackpoint" && formik.values.report_sub_type === "detailed" && (
          <>
            <QICustomSelect
              label={getLocalizedString("report_configuration", "Report Configuration")}
              value={formik.values.report_configuration_id}
              onChange={(value) => {
                formik.setFieldValue("report_configuration_id", value);
                formik.setFieldError("report_configuration_id", undefined);
              }}
              readOnly={fieldsReadOnly}
              disabled={fieldsReadOnly}
              labelClassName="report-configuration"
              error={formik.errors.report_configuration_id}
            >
              {reportConfigurations?.data?.length ? (
                <li value="">{getLocalizedString("select", "Select")}</li>
              ) : (
                <li value={null}>No Data Found</li>
              )}
              {reportConfigurations?.data?.map((config) => (
                <li key={config.id} value={config.id}>{config.name}</li>
              ))}
            </QICustomSelect>
            <FieldError error={formik.errors.report_configuration_id} />
          </>
        )}

        {formik.values.report_sub_type === "summary" && (
          <>
            <QICustomSelect
              label={getLocalizedString("summary_interval", "Summary Interval")}
              value={formik.values.summary_interval}
              onChange={(value) => {
                formik.setFieldValue("summary_interval", value);
                formik.setFieldError("summary_interval", undefined);
              }}
              readOnly={fieldsReadOnly}
              disabled={fieldsReadOnly}
              error={formik.errors.summary_interval}
            >
              {summaryIntervals.map((item) => (
                <li key={item.id} value={item.id}>{getLocalizedString(item.localization_key) || item.name}</li>
              ))}
            </QICustomSelect>
            <FieldError error={formik.errors.summary_interval} />
            <QISwitch
              label={getLocalizedString("group_by_asset", "Group By Asset")}
              value={formik.values.group_by_asset}
              onChange={() => !fieldsReadOnly && formik.setFieldValue("group_by_asset", !formik.values.group_by_asset)}
              readOnly={fieldsReadOnly}
              disabled={fieldsReadOnly}
            />
          </>
        )}

        <QICustomSelect
          label={getLocalizedString("selected_criteria", "Selected Criteria")}
          value={formik.values.metadata.selection_criteria}
          onChange={(value) => {
            formik.setFieldValue("metadata", { ...formik.values.metadata, selection_criteria: value, asset_ids: [] });
            formik.setFieldError("selection_criteria", undefined);
          }}
          readOnly={fieldsReadOnly}
          disabled={fieldsReadOnly}
          error={formik.errors.selection_criteria}
        >
          {(isAdmin ? selectionType : selectionTypeNoGroup).map((item) => (
            <li key={item.id} value={item.name}>{getLocalizedString(item.localization_key) || item.name}</li>
          ))}
        </QICustomSelect>
        <FieldError error={formik.errors.selection_criteria} />

        <QICustomSelect
          label={getLocalizedString("asset_type", "Asset Type")}
          value={formik.values.metadata.asset_type}
          onChange={(value) => {
            formik.setFieldValue("metadata", { ...formik.values.metadata, asset_type: value, asset_ids: [] });
            formik.setFieldError("asset_type", undefined);
          }}
          readOnly={fieldsReadOnly}
          disabled={fieldsReadOnly}
          error={formik.errors.asset_type}
        >
          {(isAdmin ? assetTypes : assetTypesNoDevice).map((item) => (
            <li key={item.id} value={item.name}>{getLocalizedString(item.localization_key) || item.name}</li>
          ))}
        </QICustomSelect>
        <FieldError error={formik.errors.asset_type} />

        {formik.values.metadata.selection_criteria && formik.values.metadata.asset_type && (
          <>
            <QIMultiSelectDropDown
              label={isDevice
                ? getLocalizedString(isGroup ? "devices_group" : "devices", isGroup ? "Devices Group" : "Devices")
                : getLocalizedString(isGroup ? "vehicles_group" : "vehicles", isGroup ? "Vehicles Group" : "Vehicles")}
              data={assetOptions || []}
              selected={assetIds}
              onChange={(ids) => {
                formik.setFieldValue("metadata", { ...formik.values.metadata, asset_ids: ids });
                formik.setFieldError("asset_ids", undefined);
              }}
              onSearch={searchAssets}
              readOnly={fieldsReadOnly}
              error={formik.errors.asset_ids}
            />
            <FieldError error={formik.errors.asset_ids} />
          </>
        )}

        <QIMultiSelectDropDown
          label={getLocalizedString("file_format", "File Format")}
          data={formatOptions}
          selected={formik.values.file_format}
          onChange={(ids) => {
            formik.setFieldValue("file_format", ids);
            formik.setFieldError("file_format", undefined);
          }}
          readOnly={fieldsReadOnly}
          error={formik.errors.file_format}
          className="narrow-multiselect-dropdown"
        />
        <FieldError error={formik.errors.file_format} />

        {/* Report delivery section */}
        <div className="srd-section-divider">
          <strong>{getLocalizedString("report_delivery", "Report delivery")}</strong>
        </div>

        {/* Send to - using QIMultiSelectDropDown */}
        <QIMultiSelectDropDown
          label={<>{getLocalizedString("send_to", "Send to")} <span className="srd-required">*</span></>}
          labelKey="name"
          data={isUserGroupsSuccess ? userGroups?.data : []}
          allData={isUserGroupsSuccess ? userGroups?.data : []}
          selected={formik.values.user_group_ids}
          onChange={(ids) => {
            formik.setFieldValue("user_group_ids", ids);
            formik.setFieldError("user_group_ids", undefined);
          }}
          readOnly={fieldsReadOnly}
          error={formik.errors.user_group_ids}
        />
        <FieldError error={formik.errors.user_group_ids} />
        <p className="srd-hint">{getLocalizedString("user_email_hint", "User(s) must have a valid email in user profile to receive report")}</p>

        {/* Send if empty - label inside using qi-input pattern */}
        <div className={`qi-input qi-form-element srd-send-empty-row ${fieldsReadOnly ? "disabled" : ""}`}>
          <label className="qi-input_label">{getLocalizedString("send_if_empty", "Send if empty")}</label>
          <div className="qi-input_wrapper">
            <div className="srd-send-empty">
              <span className="switch">
                <input type="checkbox" disabled={fieldsReadOnly} checked={formik.values.send_if_empty} readOnly />
                <span className="slider round" onClick={() => !fieldsReadOnly && formik.setFieldValue("send_if_empty", !formik.values.send_if_empty)} />
              </span>
              <span className="srd-send-empty_hint">{getLocalizedString("deliver_report_empty", "Deliver report even if there is no data to show")}</span>
            </div>
          </div>
        </div>

        {formik.errors.serverError && <div className="text-danger mt-2">{formik.errors.serverError}</div>}

        {editMode && (
          <div className="srd-footer">
            <QIButton className="secondary sm mr-2" onClick={toggleEditMode}>{getLocalizedString("cancel", "CANCEL")}</QIButton>
            <QIButton className="primary sm" onClick={formik.handleSubmit}>{getLocalizedString("update", "UPDATE")}</QIButton>
          </div>
        )}
      </div>
    </div>
  );
};
